import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  BadgeCheck, Building2, Phone, Users, Mail, ShieldCheck, ExternalLink,
  ArrowRight, Flag, AlertTriangle, Coins,
} from 'lucide-react';
import { Button } from '../components/ui/button';

/**
 * Public trust page at /trust — explains how PatinaMart verifies suppliers
 * and what the various badges in the marketplace mean. Designed to be the
 * link buyers and sceptical designers click on from the verified badge
 * tooltip.
 */
export default function TrustPage() {
  // Stagger the checklist reveal so it animates on first paint.
  const [visible, setVisible] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setVisible((v) => (v < CRITERIA.length ? v + 1 : v)), 220);
    return () => clearInterval(t);
  }, []);

  return (
    <div className="min-h-screen pt-24 pb-20" data-testid="trust-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        {/* Hero */}
        <div className="mb-16 max-w-3xl">
          <p className="text-xs uppercase tracking-[0.2em] text-accent mb-3 inline-flex items-center gap-2">
            <ShieldCheck className="w-3 h-3" /> Trust &amp; verification
          </p>
          <h1 className="font-playfair text-4xl md:text-6xl font-bold text-foreground leading-tight mb-5">
            Every seller, eyeballed.
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed">
            PatinaMart isn't an open marketplace where anyone with an email can post stock. Every
            supplier on the platform has been hand-verified by our team — because reclaimed
            building materials are too expensive, too heavy, and too irreplaceable to gamble on a
            stranger.
          </p>
        </div>

        {/* Two-column: criteria | animated check list */}
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 lg:gap-16 mb-20">
          {/* Left — narrative criteria */}
          <div className="space-y-7">
            <p className="text-xs uppercase tracking-[0.18em] text-accent">How we verify</p>
            {CRITERIA.map((c, i) => (
              <div key={c.title} className="flex gap-4">
                <div className="w-10 h-10 rounded-lg bg-accent/10 border border-accent/30 flex items-center justify-center flex-shrink-0">
                  <c.icon className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">
                    Step {String(i + 1).padStart(2, '0')}
                  </p>
                  <h3 className="font-playfair text-xl text-foreground mb-1.5">{c.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{c.body}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Right — sticky animated checklist + the badge */}
          <div className="lg:sticky lg:top-28 self-start">
            <div className="rounded-2xl border border-accent/30 bg-gradient-to-br from-accent/5 to-transparent p-7">
              <div className="flex items-center gap-2 mb-5">
                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-accent/15 border border-accent/40 text-accent font-medium text-sm">
                  <BadgeCheck className="w-4 h-4" />
                  Verified seller
                </span>
              </div>
              <p className="text-sm text-muted-foreground leading-relaxed mb-6">
                A supplier earns the gold "Verified seller" badge once they've cleared every check on
                the left. It appears on their listing cards, their full listing page, and their
                storefront hero — so buyers know at a glance who's been through the gate.
              </p>
              <ul className="space-y-3">
                {CRITERIA.map((c, i) => (
                  <li
                    key={c.title}
                    className={`flex items-start gap-3 transition-all duration-500 ${
                      i < visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-3'
                    }`}
                  >
                    <span className="flex-shrink-0 mt-0.5 w-5 h-5 rounded-full bg-accent/20 border border-accent/40 flex items-center justify-center">
                      <BadgeCheck className="w-3 h-3 text-accent" />
                    </span>
                    <span className="text-sm text-foreground/85">{c.short}</span>
                  </li>
                ))}
              </ul>
              <div className="border-t border-border/20 mt-6 pt-5">
                <p className="text-xs text-muted-foreground">
                  Average time from registration to verification: <span className="text-foreground/85 font-medium">24–48 hours</span>.
                  We're a small team. We answer every email.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* What the OTHER labels mean */}
        <div className="mb-20">
          <p className="text-xs uppercase tracking-[0.18em] text-accent mb-3">Other labels you'll see</p>
          <h2 className="font-playfair text-3xl md:text-4xl font-bold text-foreground mb-8">
            We're honest about what we haven't verified.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <LabelCard
              icon={ExternalLink}
              colour="text-blue-300 bg-blue-500/15 border-blue-500/30"
              title="Gumtree / Facebook Marketplace / Craigslist"
              body="Listings mirrored from external classifieds. We haven't met the seller, and 'Contact seller' redirects you back to the original page. Useful for browsing breadth, but transact with eyes wide open."
            />
            <LabelCard
              icon={AlertTriangle}
              colour="text-amber-300 bg-amber-500/15 border-amber-500/30"
              title="Awaiting verification"
              body="A supplier who's just registered but hasn't been reviewed yet. Their listings stay hidden from buyers until they pass our checks — you'll never see this label on a public listing card."
            />
            <LabelCard
              icon={Flag}
              colour="text-rose-300 bg-rose-500/15 border-rose-500/30"
              title="Spotted a problem?"
              body="Every listing carries a 'Report listing' link in the footer. Anyone can flag wrong photos, copyright issues, or scams — anonymously, no account needed. We review every report within 48 hours."
            />
          </div>
        </div>

        {/* How we make money — buyers often assume there's a hidden margin or
            gotcha. Stating it plainly removes a common objection. */}
        <div className="mb-20 rounded-2xl border border-border/20 bg-card/40 p-8 md:p-12">
          <div className="flex items-start gap-4 mb-6">
            <div className="w-11 h-11 rounded-lg bg-accent/10 border border-accent/30 flex items-center justify-center flex-shrink-0">
              <Coins className="w-5 h-5 text-accent" />
            </div>
            <div>
              <p className="text-xs uppercase tracking-[0.18em] text-accent mb-1">How we make money</p>
              <h3 className="font-playfair text-2xl md:text-3xl font-bold text-foreground">
                1% per sale. Nothing hidden.
              </h3>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            <FeeCard
              label="Buyers pay"
              amount="$0"
              body="Browsing, favouriting, sourcing calls, contacting suppliers — all free forever. The price you see is the price you pay."
            />
            <FeeCard
              label="Suppliers pay"
              amount="$0 to list"
              body="Unlimited listings, storefront, map pin, Wingman app — all free. We only take a cut when you actually sell something."
              highlight
            />
            <FeeCard
              label="Platform fee"
              amount="1%"
              body="When a sale closes through Stripe we take a flat 1% of the transaction to keep the lights on. Stripe's own processing fee is passed through — no markup."
            />
          </div>
          <p className="text-sm text-muted-foreground mt-6 leading-relaxed">
            That's it. No listing fees, no featured-placement auctions, no subscriptions, no data
            sold to advertisers. If you ever spot a charge you didn't expect, email
            {' '}<a href="mailto:contact@patinamart.com" className="text-accent hover:underline">contact@patinamart.com</a>
            {' '}and we'll refund it on the spot while we investigate.
          </p>
        </div>

        {/* CTA */}
        <div className="rounded-2xl border border-border/20 bg-card/30 p-8 md:p-12 text-center">
          <h3 className="font-playfair text-2xl md:text-3xl font-bold text-foreground mb-3">
            Run a salvage yard? Apply for verification.
          </h3>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-6">
            Verified suppliers get a public storefront, the gold badge, listings on the map, the
            mobile Wingman app, and direct access to designers via Sourcing Calls. Free for the
            first 12 months while we grow the network.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/auth?mode=register&type=supplier">
              <Button className="btn-gold" data-testid="trust-signup-cta">
                Become a supplier <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <a href="mailto:contact@patinamart.com" className="text-sm text-accent hover:underline inline-flex items-center gap-1">
              <Mail className="w-4 h-4" /> Email us first
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

const CRITERIA = [
  {
    icon: Building2,
    title: 'Real business, real address',
    short: 'Active business registration + a physical yard or showroom',
    body: 'We verify an active business registration (ABN in Australia, Companies House in the UK, etc.) and confirm the supplier operates from a real, visitable physical location — not a PO box.',
  },
  {
    icon: Phone,
    title: 'A human picks up the phone',
    short: 'A real conversation about their stock and trade history',
    body: "We pick up the phone and talk to the owner. We ask about their typical stock, where they source from, and what salvage means to them. Five minutes is usually enough — chancers don't last past minute two.",
  },
  {
    icon: Users,
    title: 'At least one referral',
    short: 'A designer, builder, or buyer they\'ve traded with vouches for them',
    body: "We ask for a designer, builder, or repeat buyer who's bought from them recently. We email that referral and confirm they're happy. If a supplier has zero referrals, we ask them to start small and build a track record.",
  },
  {
    icon: ShieldCheck,
    title: 'Spot-check on listings',
    short: 'A handful of their first listings reviewed for honesty',
    body: 'Once approved, we eyeball their first batch of listings. Wrong-photo bait-and-switch, AI-generated stock images, and obvious off-topic posts get flagged before any buyer ever sees them.',
  },
];

function LabelCard({ icon: Icon, colour, title, body }) {
  return (
    <div className="rounded-xl border border-border/20 bg-card/40 p-5">
      <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full border mb-3 ${colour}`}>
        <Icon className="w-3 h-3" />
        Label
      </span>
      <h4 className="font-medium text-foreground mb-2">{title}</h4>
      <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
    </div>
  );
}

function FeeCard({ label, amount, body, highlight = false }) {
  return (
    <div className={`rounded-xl border p-5 ${highlight ? 'border-accent/40 bg-accent/5' : 'border-border/20 bg-background/30'}`}>
      <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className={`font-playfair text-3xl font-bold mt-1 mb-3 ${highlight ? 'text-accent' : 'text-foreground'}`}>
        {amount}
      </p>
      <p className="text-sm text-muted-foreground leading-relaxed">{body}</p>
    </div>
  );
}
